import React, { useMemo, useState } from 'react';
import {
  Popover,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const DEFAULT_LIMIT = 10;

/**
 * Saved-prompts picker, opened from a chip at the top of the chat panel --
 * moved out of the always-open left sidebar section per the same declutter
 * direction as the workspace Menu chip. Reuses the parent's existing
 * quick-menu state/handlers (usage tracking, add/edit/delete, top-10 +
 * "see more") rather than duplicating that logic here.
 * See project_prompts_moved_to_chat_panel.
 */
function PromptsMenuPopover({
  anchorEl,
  onClose,
  colors,
  items, // userQuickMenuItems (or defaultQuickActions fallback), each {id, buttonText, sentence, isUserItem, usageCount}
  onUse, // (item) => void -- populates chat input, closes popover
  onSend, // (item) => void -- sends immediately, closes popover
  onEdit,
  onDelete,
  onAdd,
}) {
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const open = Boolean(anchorEl);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((item) =>
      (item.buttonText && item.buttonText.toLowerCase().includes(q)) ||
      (item.sentence && item.sentence.toLowerCase().includes(q))
    );
  }, [items, search]);

  const isCapped = !search.trim() && !showAll && filtered.length > DEFAULT_LIMIT;
  const display = isCapped
    ? [...filtered].sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0)).slice(0, DEFAULT_LIMIT)
    : filtered;

  const handleClose = () => {
    setSearch('');
    setShowAll(false);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{ sx: { width: 340, bgcolor: colors.surface, border: `1px solid ${colors.border}` } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: `1px solid ${colors.border}` }}>
        <BoltIcon sx={{ fontSize: 18, color: '#22c55e' }} />
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, flex: 1 }}>
          My Prompts
        </Typography>
        <Tooltip title="Add prompt">
          <IconButton size="small" onClick={onAdd} sx={{ color: colors.textMuted, '&:hover': { color: '#22c55e' } }}>
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ p: 1.5 }}>
        <TextField
          size="small"
          autoFocus
          placeholder="Search my prompts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': { bgcolor: colors.background, fontSize: '0.85rem' },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ maxHeight: showAll || search ? 400 : 260, overflow: 'auto' }}>
          {display.length === 0 ? (
            <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', textAlign: 'center', py: 1 }}>
              {search ? 'No matching prompts' : 'No prompts saved yet'}
            </Typography>
          ) : (
            display.map((item, index) => (
              <Box
                key={item.id || index}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.75,
                  borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: colors.hoverBg },
                  '&:hover .prompt-actions': { opacity: 1 },
                }}
                onClick={() => { onUse(item); handleClose(); }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', flex: 1 }}>
                  <BoltIcon sx={{ fontSize: 14, color: '#22c55e', flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    sx={{ color: colors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={item.sentence}
                  >
                    {item.buttonText}
                  </Typography>
                </Box>
                <Box className="prompt-actions" sx={{ display: 'flex', gap: 0.25, opacity: 0, transition: 'opacity 0.2s' }}>
                  <Tooltip title="Send now">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onSend(item); handleClose(); }}
                      sx={{ color: colors.textMuted, '&:hover': { color: '#22c55e' } }}
                    >
                      <SendIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  {item.isUserItem && (
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                          sx={{ color: colors.textMuted, '&:hover': { color: '#3b82f6' } }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                          sx={{ color: colors.textMuted, '&:hover': { color: '#ef4444' } }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
        {!search.trim() && filtered.length > DEFAULT_LIMIT && (
          <Typography
            variant="caption"
            onClick={() => setShowAll((prev) => !prev)}
            sx={{ display: 'block', textAlign: 'center', color: '#22c55e', cursor: 'pointer', pt: 0.75, '&:hover': { textDecoration: 'underline' } }}
          >
            {showAll ? 'Show less' : `See more (${filtered.length - DEFAULT_LIMIT} more)`}
          </Typography>
        )}
      </Box>
    </Popover>
  );
}

export default PromptsMenuPopover;
